import { useState } from 'react';

import { logger, type SourceProposedActionT } from '@/shared/lib';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';

interface SubmittedLabOrder {
  remoteOrderId: string;
}

interface PendingProposedActionFinalization {
  order: SubmittedLabOrder;
  source: SourceProposedActionT;
}

const pendingFinalizations = new Map<string, PendingProposedActionFinalization>();

const SUBMITTED_ORDER_FINALIZATION_ERROR =
  'The lab order was submitted, but Cortex could not update the proposed action. Do not submit the order again.';

export function useLabOrderFinalization({
  userId,
  sourceProposedAction,
}: {
  userId: string;
  sourceProposedAction?: SourceProposedActionT;
}) {
  const recoveryKey = sourceProposedAction?.externalId
    ? JSON.stringify([userId, sourceProposedAction.externalId])
    : undefined;
  const recovered = recoveryKey ? pendingFinalizations.get(recoveryKey) : undefined;
  const source = recovered?.source ?? sourceProposedAction;
  const [submittedOrder, setSubmittedOrder] = useState<SubmittedLabOrder | null>(
    recovered?.order ?? null
  );
  const [isFinalizing, setIsFinalizing] = useState(false);
  const mutations = useProposedActionMutations();

  const finalize = async (order: SubmittedLabOrder): Promise<boolean> => {
    if (!source || !recoveryKey) {
      return true;
    }

    pendingFinalizations.set(recoveryKey, { order, source });
    setSubmittedOrder(null);
    setIsFinalizing(true);
    try {
      await mutations.finalizeFromSource(source, { remoteOrderId: order.remoteOrderId });
      pendingFinalizations.delete(recoveryKey);
      return true;
    } catch (error: unknown) {
      logger.error(
        'labOrderWizard.finalizeFromSource.failed',
        {
          externalId: source.externalId,
        },
        error
      );
      setSubmittedOrder(order);
      return false;
    } finally {
      setIsFinalizing(false);
    }
  };

  return {
    recoveryKey,
    submittedOrder,
    errorMessage: submittedOrder ? SUBMITTED_ORDER_FINALIZATION_ERROR : null,
    isFinalizing,
    finalize,
    retry: async (): Promise<boolean> => (submittedOrder ? finalize(submittedOrder) : false),
  };
}
